import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { ModalTypes } from "@enums/modal-types";
import { CellDropdownItem } from "@idw-types/common";
import { RpServiceChangeRequest } from "@idw-types/data/rp-service-change-request";
import { VerificationServiceChangeRequest } from "@idw-types/data/verification-service";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { VerificationServiceDetailsValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { createVerificationServiceChangeRequestRequest } from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import {
    clickVerificationServiceChangeRequestActionHandler,
    deployVerificationService,
    performVerificationServiceChangeRequestStep
} from "@utils/change-request-modal-util/verification-service-change-request-modal-util";
import styles from "./verification-service-details-dropdown-menu.less";
import { useChangeRequest } from "@hooks/use-change-requests";


type VerificationServiceDetailsDropdownMenuProps = {
    stateIsUnchanged: boolean;
    validationErrors: VerificationServiceDetailsValidationErrors;
    requestInProgress: boolean;
    rpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceChangeRequests: Array<RpServiceChangeRequest>;
    setRequestInProgress: (requestInProgress: boolean) => void;
    setRpServiceConfiguration: (rpServiceConfiguration: FormattedRpServiceConfiguration) => void;
    setRpServiceChangeRequest: (changeRequest: RpServiceChangeRequest) => void;
}

export const VerificationServiceDetailsDropdownMenu = (props: VerificationServiceDetailsDropdownMenuProps) => {
    const {
        stateIsUnchanged,
        validationErrors,
        rpServiceConfiguration,
        rpServiceChangeRequests,
        requestInProgress,
        setRequestInProgress,
        setRpServiceConfiguration,
        setRpServiceChangeRequest
    } = props;

    const dispatch = useDispatch();
    const modalContext = useModalContext();

    const {
        changeRequestStatus,
        changeRequestType
    } = useChangeRequest({ changeRequests: rpServiceChangeRequests });


    const createVerificationServiceDetailChangeRequest = async () => {
        const { data, error } = await createVerificationServiceChangeRequestRequest(rpServiceConfiguration.id);
        if (data?.changeRequest) {
            displaySuccessModal(
                dispatch,
                resources.requests.createVerificationServiceChangeRequest.success.title,
                resources.requests.createVerificationServiceChangeRequest.success.message
            );

            setRpServiceChangeRequest(data.changeRequest);
            return data.changeRequest;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createVerificationServiceChangeRequest.error.title,
                resources.requests.createVerificationServiceChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onSubmitCreateVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await createVerificationServiceDetailChangeRequest();

        if (response) {
            onModalClose();
        }
        return response;
    }

    const onClickCreateAModifyChangeRequest = async () => {
        return clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitCreateVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.CreateVerificationServiceChangeRequest
        );
    };

    const handleSuccessfulChangeRequestAction = (
        newChangeRequest: VerificationServiceChangeRequest,
        onModalClose: () => void
    ) => {
        displaySuccessModal(
            dispatch,
            resources.requests.updateVerificationServiceChangeRequest.success.title,
            resources.requests.updateVerificationServiceChangeRequest.success.message
        );
        setRpServiceConfiguration(rpServiceConfiguration);
        setRpServiceChangeRequest(newChangeRequest);
        onModalClose();
    };

    const onSubmitSubmitVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceConfiguration.id,
            ChangeRequestStatusTypes.SUBMITTED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    };

    const onClickSubmitChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitSubmitVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.SubmitVerificationServiceChangeRequest
        );
    };

    const onSubmitUnsubmitVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceConfiguration.id,
            ChangeRequestStatusTypes.UNSUBMITTED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    };

    const onClickUnsubmitChangeRequest = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitUnsubmitVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.UnsubmitVerificationServiceChangeRequest
        );
    };

    const onClickReviewChangeRequest = async () => {
        if (changeRequestStatus !== ChangeRequestStatusTypes.UNDER_REVIEW) {
            setRequestInProgress(true);
            const response = await performVerificationServiceChangeRequestStep(
                dispatch,
                rpServiceConfiguration.id,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                null
            );

            if (response) {
                setRpServiceConfiguration(rpServiceConfiguration);
                setRpServiceChangeRequest(response);
            }
            setRequestInProgress(false);
        }
    };

    const onSubmitApproveVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceConfiguration.id,
            ChangeRequestStatusTypes.APPROVED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }
        return response;
    };

    const onClickApproveChanges = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitApproveVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.ApproveVerificationServiceChangeRequest
        );
    };

    const onSubmitRejectVerificationServiceChangeRequest = async (onModalClose: () => void, data: string) => {
        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceConfiguration.id,
            ChangeRequestStatusTypes.REJECTED,
            { comments: data }
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }
        return response;
    };

    const onClickRejectChanges = () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitRejectVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.RejectVerificationServiceChangeRequest
        );
    };


    const onSubmitCancelVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceConfiguration.id,
            ChangeRequestStatusTypes.CANCELED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    }


    const onClickCancelChangeRequest = async () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitCancelVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.CancelVerificationServiceChangeRequest
        )
    };

    const onSubmitDeployNowVerificationServiceChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(changeRequestStatus);
        const response = await deployVerificationService(dispatch, rpServiceConfiguration.id, remainingChangeRequestStatuses, 0);

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    }

    const onClickDeployChangesNow = async () => {
        clickVerificationServiceChangeRequestActionHandler(
            dispatch,
            onSubmitDeployNowVerificationServiceChangeRequest,
            modalContext,
            rpServiceConfiguration.id,
            rpServiceConfiguration.serviceName,
            rpServiceConfiguration.apiKey,
            ModalTypes.DeployNowVerificationServiceChangeRequest
        );
    };

    const verificationServiceDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];

        const {
            permissions
        } = rpServiceConfiguration;

        const prefix = 'verification-service-details-dropdown-item';

        if ((
            permissions.canSubmit && permissions.canReview && permissions.canDeploy && changeRequestStatus &&
            [
                ChangeRequestStatusTypes.UNSUBMITTED,
                ChangeRequestStatusTypes.SUBMITTED,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                ChangeRequestStatusTypes.APPROVED
            ].includes(changeRequestStatus)
        ) || (permissions.canDeploy && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.APPROVED)) {
            let disabledText = resources.common.dropdownItems.deployChangesNow.unsavedChangesTooltip;
            if (validationErrors.count > 0) {
                disabledText = resources.common.dropdownItems.deployChangesNow.validationErrorsTooltip;
            }

            items.push({
                idSuffix: `${prefix}-deploy-changes-now`,
                title: resources.common.dropdownItems.deployChangesNow.label,
                text: resources.common.dropdownItems.deployChangesNow.label,
                disabled: !stateIsUnchanged || validationErrors.count > 0,
                disabledText: disabledText,
                onClick: onClickDeployChangesNow
            });
        }

        if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
            items.push({
                idSuffix: `${prefix}-create-modify-change-request`,
                title: resources.common.dropdownItems.createModifyChangeRequest.label,
                text: resources.common.dropdownItems.createModifyChangeRequest.label,
                onClick: onClickCreateAModifyChangeRequest
            });
        }

        if (permissions.canSubmit && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED) {
            let disabledText = resources.common.dropdownItems.submitChangeRequestForReview.unsavedChangesTooltip;
            if (validationErrors.count > 0) {
                disabledText = resources.common.dropdownItems.submitChangeRequestForReview.validationErrorsTooltip;
            }

            items.push({
                idSuffix: `${prefix}-submit-change-request`,
                title: resources.common.dropdownItems.submitChangeRequestForReview.label,
                text: resources.common.dropdownItems.submitChangeRequestForReview.label,
                disabled: !stateIsUnchanged || validationErrors.count > 0,
                disabledText: disabledText,
                onClick: onClickSubmitChangeRequest
            });
        }

        if (
            permissions.canSubmit &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            items.push({
                idSuffix: `${prefix}-unsubmit-change-request`,
                title: resources.common.dropdownItems.unsubmitChangeRequest.label,
                text: resources.common.dropdownItems.unsubmitChangeRequest.label,
                onClick: onClickUnsubmitChangeRequest
            });
        }

        if (
            permissions.canReview &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED].includes(changeRequestStatus)
        ) {
            items.push(
                {
                    idSuffix: `${prefix}-review-change-request`,
                    title: resources.common.dropdownItems.reviewChangeRequest.label,
                    text: resources.common.dropdownItems.reviewChangeRequest.label,
                    onClick: onClickReviewChangeRequest
                }
            );
        }

        if (
            permissions.canReview &&
            changeRequestType &&
            [ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            items.push(
                {
                    idSuffix: `${prefix}-approve-change-request`,
                    title: resources.common.dropdownItems.approveChangeRequest.label,
                    text: resources.common.dropdownItems.approveChangeRequest.label,
                    onClick: onClickApproveChanges
                },
                {
                    idSuffix: `${prefix}-reject-change-request`,
                    title: resources.common.dropdownItems.rejectChangeRequest.label,
                    text: resources.common.dropdownItems.rejectChangeRequest.label,
                    onClick: onClickRejectChanges
                },
            );
        }

        if (permissions.canCancel && changeRequestType && ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
        ) {
            items.push({
                idSuffix: `${prefix}-cancel-change-request`,
                title: resources.common.dropdownItems.cancelChangeRequest.label,
                text: resources.common.dropdownItems.cancelChangeRequest.label,
                onClick: onClickCancelChangeRequest
            });
        }

        return items;
    }, [
        rpServiceConfiguration,
        changeRequestStatus,
        changeRequestType,
        stateIsUnchanged,
        validationErrors
    ]);

    return (
        <TableCellWithDropdown
            className={styles.verificationServiceDetailsDropdownMenu}
            value={""}
            dropdownText={"Actions"}
            disabled={requestInProgress}
            items={verificationServiceDropdownItems}
            uniqueId={rpServiceConfiguration.id}
        />
    );

}